(() => {
  if (window.gpSuppliesCollection) return;
  window.gpSuppliesCollection = true;
  const states = new WeakMap();
  const pendingForms = new WeakSet();
  const text = (root, en, zh) => root.dataset.lang === 'zh' ? zh : en;
  const key = url => url.pathname + url.search;

  function localURL(root, value) {
    const url = new URL(value, location.href);
    const base = new URL(root.dataset.collectionUrl, location.origin).pathname.replace(/\/$/, '');
    return url.origin === location.origin && (url.pathname === base || url.pathname.startsWith(base + '/')) ? url : null;
  }

  function stateFor(root) {
    if (!states.has(root)) states.set(root, { sequence: 0, cache: new Map([[key(new URL(location.href)), root.innerHTML]]) });
    return states.get(root);
  }

  async function navigate(root, url, historyMode = 'push', pagination = false) {
    const state = stateFor(root), sequence = ++state.sequence;
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    root.setAttribute('aria-busy', 'true');
    const status = root.querySelector('[data-supply-status]');
    status.textContent = text(root, 'Updating products…', '正在更新商品…');
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      let html = state.cache.get(key(url));
      if (!html) {
        const endpoint = new URL(url);
        endpoint.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(endpoint, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw Error('Section request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector('[data-supplies]');
        if (!next || next.dataset.lang !== root.dataset.lang || next.dataset.collectionUrl !== root.dataset.collectionUrl || next.innerHTML.includes('Liquid error')) throw Error('Invalid collection response');
        html = next.innerHTML;
      }
      if (sequence !== state.sequence) return;
      state.cache.set(key(url), html);
      if (state.cache.size > 12) state.cache.delete(state.cache.keys().next().value);
      const focusId = document.activeElement?.id;
      root.innerHTML = html;
      if (historyMode === 'push' && key(url) !== key(new URL(location.href))) history.pushState(history.state, '', url);
      root.querySelector('[data-supply-status]').textContent = text(root, 'Showing ', '顯示 ') + root.querySelector('[data-supply-count]').textContent.trim();
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      if (pagination) {
        const toolbar = root.querySelector('.gpsup-catalogbar');
        toolbar.setAttribute('tabindex', '-1');
        toolbar.focus({ preventScroll: true });
        toolbar.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    } catch (error) {
      if (sequence !== state.sequence) return;
      const message = root.querySelector('[data-supply-status]');
      message.textContent = text(root, 'Unable to refresh. ', '暫時未能更新。');
      const link = document.createElement('a');
      link.href = url.href;
      link.textContent = text(root, 'Open these results', '開啟篩選結果');
      message.append(link);
    } finally {
      clearTimeout(timer);
      if (sequence === state.sequence) root.removeAttribute('aria-busy');
    }
  }

  function updateVariant(select) {
    const card = select.closest('[data-supply-card]');
    const root = card.closest('[data-supplies]');
    const option = select.selectedOptions[0];
    card.querySelector('[data-supply-price]').textContent = option.dataset.price;
    card.querySelector('[data-supply-pack]').textContent = option.dataset.pack;
    card.querySelectorAll('[data-product-link]').forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('variant', option.value);
      link.href = url.href;
    });
    const image = card.querySelector('.gpsup-card__photo img');
    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }
    const button = card.querySelector('[data-supply-add]');
    button.disabled = option.dataset.available !== 'true';
    button.textContent = button.disabled ? text(root, 'Sold out', '暫時售罄') : text(root, 'Add to cart', '加入購物車');
    card.querySelector('[data-cart-message]').textContent = '';
  }

  async function addToCart(form, root) {
    if (pendingForms.has(form)) return;
    pendingForms.add(form);
    const button = form.querySelector('[data-supply-add]');
    const message = form.querySelector('[data-cart-message]');
    const select = form.querySelector('select[name="id"]');
    const id = String(new FormData(form).get('id'));
    const routeRoot = root.dataset.rootUrl.replace(/\/?$/, '/');
    const drawer = document.querySelector('hdt-cart-drawer[ref="hdt-cart"]');
    const sectionId = drawer?.getAttribute('section-id');
    const body = { items: [{ id, quantity: 1 }], sections_url: location.pathname };
    if (sectionId) body.sections = [sectionId];
    button.disabled = true;
    if (select) select.disabled = true;
    button.textContent = text(root, 'Adding…', '正在加入…');
    message.textContent = '';
    let confirmed = false;
    try {
      const response = await fetch(routeRoot + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok || result.status) {
        message.textContent = result.description || text(root, 'Unable to add this pack. Check availability and try again.', '未能加入此規格，請確認供應情況後再試。');
        return;
      }
      confirmed = true;
      message.textContent = text(root, 'Added. ', '已加入。');
      const cartLink = document.createElement('a');
      cartLink.href = root.dataset.cartUrl;
      cartLink.textContent = text(root, 'View cart', '查看購物車');
      message.append(cartLink);
      if (sectionId && result.sections?.[sectionId]) root.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { cartData: result, source: 'supplies', actionAfterATC: 'no_action', data: { sections: result.sections } } }));
      try {
        const cartResponse = await fetch(routeRoot + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.querySelectorAll('hdt-cart-count').forEach(el => { el.textContent = cart.item_count; });
        }
      } catch { /* Addition is already confirmed; keep the cart link available. */ }
    } catch {
      if (!confirmed) {
        message.textContent = text(root, 'We could not confirm the result. Check your cart before adding again. ', '暫時未能確認結果，請先查看購物車，避免重複加入。');
        const link = document.createElement('a');
        link.href = root.dataset.cartUrl;
        link.textContent = text(root, 'View cart', '查看購物車');
        message.append(link);
      }
    } finally {
      pendingForms.delete(form);
      if (select) select.disabled = false;
      button.disabled = select ? select.selectedOptions[0].dataset.available !== 'true' : false;
      button.textContent = button.disabled ? text(root, 'Sold out', '暫時售罄') : text(root, 'Add to cart', '加入購物車');
    }
  }

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[data-supply-nav]');
    const root = link?.closest('[data-supplies]');
    if (!root || (link.target && link.target !== '_self')) return;
    const url = localURL(root, link.href);
    if (!url) return;
    event.preventDefault();
    // Keep alternate-template previews on their own view while testing.
    const view = new URL(location.href).searchParams.get('view');
    if (view === 'gp-supplies') url.searchParams.set('view', view);
    navigate(root, url, 'push', !!link.closest('.gpsup-pagination') || !!link.closest('.gpsup-guide'));
  });
  document.addEventListener('change', event => {
    const select = event.target;
    const root = select.closest('[data-supplies]');
    if (!root) return;
    if (select.matches('[data-supply-variant]')) updateVariant(select);
    if (select.matches('[data-supply-sort]')) {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      navigate(root, url);
    }
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    const root = form.closest('[data-supplies]');
    if (!root || !form.matches('[data-supply-form]') || !window.fetch) return;
    event.preventDefault();
    addToCart(form, root);
  });
  window.addEventListener('popstate', () => document.querySelectorAll('[data-supplies]').forEach(root => {
    const url = localURL(root, location.href);
    if (url) navigate(root, url, 'none');
  }));
  // Capture the server-rendered state before any variant dropdown can change it.
  document.querySelectorAll('[data-supplies]').forEach(stateFor);
})();
